import ServiceTypeLinks from "@/components/ServiceTypeLinks";
import { useState, useEffect, useMemo, lazy, Suspense } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useLang } from "@/lib/language";
import { useSEO } from "@/hooks/useSEO";
import LiteYouTube from "@/components/LiteYouTube";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import BlogCard from "@/components/BlogCard";

import HeroNextStep from "@/components/HeroNextStep";

import {
  Phone,
  Shield,
  Star,
  Clock,
  CheckCircle2,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  X,
  ChevronDown,
  Menu,
  MapPin,
  Award,
  Home as HomeIcon,
  Layers,
  FileText,
  Hammer,
  Quote,
  Globe,
  Landmark,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import logo from "@/assets/logo.png";
type HeroSlide = { base: string; alt: string; widths?: number[]; src?: string };
const heroSlides: HeroSlide[] = [
  { base: "/images/projects/chula-vista/021A0047", alt: "Detached ADU completed in Chula Vista" },
  { base: "/images/projects/san-marcos/DJI_20240911130729_0011_D-2-scaled", alt: "Aerial view of detached ADU completed in San Marcos" },
  { base: "/images/projects/bonita/DSC02154-1", alt: "Winnetka Detached ADU completed in Bonita" },
  { base: "/images/sunny/exterior-front", alt: "Sunny Detached ADU exterior in Vista" },
];
import { translations, t, type Lang } from "@/lib/translations";
import { blogPosts } from "@/data/blog-posts";

const AduMap = lazy(() => import("@/components/AduMap"));
import ReviewsBar from "@/components/ReviewsBar";
import GoogleReviewBadge from "@/components/GoogleReviewBadge";
import RoiMiniCalc from "@/components/RoiMiniCalc";

/* ─── ICONS FOR SERVICES (index-matched) ───────────────────── */
const serviceIcons = [FileText, Layers, Hammer, HomeIcon];
const benefitIcons = [Award, Clock, Shield, CheckCircle2];

/* ─── STATIC DATA ──────────────────────────────────────────── */

const testimonials = [
  {
    name: "Sandra",
    location: "San Diego Area",
    rating: 5,
    text: "Elias and his team were professional, competent and easy to work with. We would like to underline the kindness and honesty of Elias and his team.",
  },
  {
    name: "Sarah J.",
    location: "San Diego Area",
    rating: 5,
    text: "EG Construction made our ADU project a breeze. Their knowledge of local regulations and their commitment to quality are unmatched.",
  },
  {
    name: "Teresa",
    location: "San Diego Area",
    rating: 5,
    text: "I was looking for true value for my money, and that's exactly what I found. The team was professional and delivered beautiful work.",
  },
  {
    name: "Allen",
    location: "San Diego Area",
    rating: 5,
    text: "I really appreciate the care they took to provide a high-quality job while completing it within our agreed time schedule.",
  },
  {
    name: "Maria",
    location: "San Diego Area",
    rating: 5,
    text: "Elias was very accommodating and they did a fabulous job.",
  },
  {
    name: "Sierra",
    location: "San Diego Area",
    rating: 5,
    text: "Very helpful and professional. The communication was consistent and friendly.",
  },
  {
    name: "Kimberly",
    location: "San Diego Area",
    rating: 5,
    text: "The project was completed promptly and done at the estimated price.",
  },
];

const statValues = ["50+", "10+", "4.8★", "60"];

const areaLinks = [
  { name: "San Diego", slug: "san-diego" },
  { name: "Chula Vista", slug: "chula-vista" },
  { name: "San Marcos", slug: "san-marcos" },
  { name: "Bonita", slug: "bonita" },
  { name: "La Mesa", slug: "la-mesa" },
  { name: "Carlsbad", slug: "carlsbad" },
  { name: "Encinitas", slug: "encinitas" },
  { name: "Escondido", slug: "escondido" },
  { name: "Poway", slug: "poway" },
  { name: "Vista", slug: "vista" },
  { name: "San Diego County", slug: "san-diego-county" },
];

/* ─── COMPONENT ─────────────────────────────────────────────── */

const Home = () => {
  const isMobile = useIsMobile();
  const [showForm, setShowForm] = useState(false);
  const [prefill, setPrefill] = useState<{ firstName?: string; phone?: string; timing?: string }>({});
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [reviewIndex, setReviewIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const { lang, toggleLang } = useLang();
  const [heroSlide, setHeroSlide] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const timer = setInterval(() => setHeroSlide((p) => (p + 1) % heroSlides.length), 5000);
    return () => clearInterval(timer);
  }, []);

  // Open lead modal when navigated with ?contact=1 (from sticky CTA / exit-intent)
  useEffect(() => {
    if (searchParams.get("contact") === "1") {
      setShowForm(true);
      const next = new URLSearchParams(searchParams);
      next.delete("contact");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);


  const T = translations;

  const homeJsonLd = useMemo(() => [
    {
      "@context": "https://schema.org",
      "@type": "GeneralContractor",
      "@id": "https://egconstructionsd.com/#business",
      name: "EG Construction",
      alternateName: "EG Construction SD",
      description: "San Diego County's #1 ADU Builder — 50+ units built, licensed & insured. Founded by Elias Gutierrez. CA License #864642.",
      url: "https://egconstructionsd.com",
      telephone: "+16198154787",
      image: "https://egconstructionsd.com/wp-content/uploads/2026/01/021A0047-1.webp",
      logo: "https://egconstructionsd.com/assets/logo.png",
      priceRange: "$$$$",
      foundingDate: "2014",
      founder: {
        "@type": "Person",
        name: "Elias Gutierrez",
        jobTitle: "Licensed General Contractor",
        worksFor: { "@id": "https://egconstructionsd.com/#business" },
      },
      knowsLanguage: ["English", "Spanish"],
      slogan: "San Diego's most trusted ADU builder",
      identifier: { "@type": "PropertyValue", name: "California Contractor License", value: "864642" },
      address: {
        "@type": "PostalAddress",
        streetAddress: "2434 Fenton St Ste 300",
        addressLocality: "Chula Vista",
        addressRegion: "CA",
        postalCode: "91914",
        addressCountry: "US",
      },
      geo: { "@type": "GeoCoordinates", latitude: 32.6401, longitude: -116.9992 },
      aggregateRating: { "@type": "AggregateRating", ratingValue: "4.8", reviewCount: "50", bestRating: "5" },
      areaServed: [
        { "@type": "City", name: "Chula Vista" },
        { "@type": "City", name: "San Marcos" },
        { "@type": "City", name: "La Mesa" },
        { "@type": "City", name: "Carlsbad" },
        { "@type": "City", name: "Encinitas" },
        { "@type": "City", name: "Escondido" },
        { "@type": "City", name: "Poway" },
        { "@type": "City", name: "Vista" },
        { "@type": "City", name: "San Diego" },
        { "@type": "City", name: "Bonita" },
      ],
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "ADU Construction Services",
        itemListElement: [
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Detached ADU Construction",
              description: "Standalone ADU built on the same lot as the primary residence. 400–1,200 sq ft.",
              provider: { "@id": "https://egconstructionsd.com/#business" },
              areaServed: { "@type": "AdministrativeArea", name: "San Diego County" },
            },
          },
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Attached ADU Construction",
              description: "ADU sharing a wall with the primary home. 400–1,000 sq ft.",
              provider: { "@id": "https://egconstructionsd.com/#business" },
              areaServed: { "@type": "AdministrativeArea", name: "San Diego County" },
            },
          },
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Garage Conversion ADU",
              description: "Existing garage converted into a livable ADU. Fastest path to a permitted unit.",
              provider: { "@id": "https://egconstructionsd.com/#business" },
              areaServed: { "@type": "AdministrativeArea", name: "San Diego County" },
            },
          },
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Junior ADU (JADU)",
              description: "ADU up to 500 sq ft built within the existing primary home footprint.",
              provider: { "@id": "https://egconstructionsd.com/#business" },
              areaServed: { "@type": "AdministrativeArea", name: "San Diego County" },
            },
          },
        ],
      },
      sameAs: [
        "https://egconstructionsd.com",
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "EG Construction",
      url: "https://egconstructionsd.com",
      inLanguage: ["en-US", "es-US"],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: T.faq.items.map((item) => ({
        "@type": "Question",
        name: t(item.q, lang),
        acceptedAnswer: { "@type": "Answer", text: t(item.a, lang) },
      })),
      speakable: {
        "@type": "SpeakableSpecification",
        cssSelector: ["#faq .faq-question", "#faq .faq-answer", "[data-faq-question]", "[data-faq-answer]"],
      },
    },
  ], [lang, T.faq.items]);


  useSEO({
    title: lang === "en"
      ? "ADU Builders San Diego | 50+ Built, 60-Day Move-In"
      : "Constructores de ADU San Diego | 50+ Construidas",
    description: lang === "en"
      ? "San Diego's licensed ADU builder (Lic #864642). 50+ ADUs built, 60-day move-in, price guarantee, 4.8★ rated. Schedule your discovery call today."
      : "Constructor de ADU licenciado en San Diego (Lic #864642). 50+ construidas, mudanza en 60 días, garantía de precio, 4.8★. Agenda tu consulta hoy.",
    lang,
    jsonLd: homeJsonLd,
  });


  const statLabels = [
    t(T.stats.adus, lang),
    t(T.stats.experience, lang),
    t(T.stats.rating, lang),
    t(T.stats.buildTime, lang),
  ];

  // Cloned track for infinite loop
  const clonedTestimonials = [
    ...testimonials.slice(-3),
    ...testimonials,
    ...testimonials.slice(0, 3),
  ];
  const offset = 3;

  const prevReview = () => {
    setIsTransitioning(true);
    setReviewIndex((i) => i - 1);
  };
  const nextReview = () => {
    setIsTransitioning(true);
    setReviewIndex((i) => i + 1);
  };

  useEffect(() => {
    if (reviewIndex < 0) {
      const timer = setTimeout(() => {
        setIsTransitioning(false);
        setReviewIndex(testimonials.length - 1);
      }, 500);
      return () => clearTimeout(timer);
    }
    if (reviewIndex > testimonials.length - 3) {
      const timer = setTimeout(() => {
        setIsTransitioning(false);
        setReviewIndex(0);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [reviewIndex]);


  return (
    <div className="min-h-screen bg-background font-body">
      <main>
      {/* ── NAVBAR ───────────────────────────────────────────── */}
      <Navbar lang={lang} onToggleLang={toggleLang} onOpenForm={() => setShowForm(true)} />

      {/* ── HERO ─────────────────────────────────────────────── */}
      <section id="hero" className="relative pt-16 bg-background overflow-hidden">
        {/* Decorative glow */}
        <div aria-hidden className="pointer-events-none absolute top-0 right-0 w-[40rem] h-[40rem] bg-primary/10 blur-[120px] rounded-full -z-0" />

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="flex flex-col items-center text-center space-y-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-primary opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
              </span>
              <span className="text-[10px] font-heading font-bold uppercase tracking-widest text-primary">
                {t(T.hero.badge, lang)}
              </span>
            </div>

            <h1 className="text-4xl sm:text-6xl lg:text-7xl font-heading font-extrabold leading-[1.05] tracking-tight text-foreground max-w-4xl">
              {t(T.hero.headline1, lang)}{" "}
              <span className="text-primary italic font-heading">{t(T.hero.headline2, lang)}</span>
            </h1>

            <p className="text-lg text-muted-foreground max-w-2xl leading-relaxed">
              {t(T.hero.subtitle, lang)}
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
              <button
                onClick={() => setShowForm(true)}
                className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-primary text-primary-foreground font-heading font-bold text-base shadow-lg shadow-primary/30 hover:bg-primary/90 hover:scale-[1.02] transition-all"
              >
                {t(T.floorPlans.primaryCta, lang)}
                <ArrowRight className="h-4 w-4" />
              </button>
              <a
                href="tel:+16198154787"
                className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full border border-border bg-background/60 backdrop-blur text-foreground font-heading font-semibold text-base hover:bg-muted transition-colors"
              >
                {lang === "en" ? "Call +1 619-815-4787" : "Llamar +1 619-815-4787"}
              </a>
            </div>

            {/* Compact trust row */}
            <div className="flex items-center gap-4 pt-2">
              <div className="flex -space-x-2">
                {["Sarah J.", "Teresa", "Allen"].map((name) => (
                  <div key={name} className="ring-2 ring-background rounded-full">
                    <GoogleReviewBadge name={name} size={36} />
                  </div>
                ))}
              </div>
              <div className="text-left">
                <div className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <Star key={s} className="h-3.5 w-3.5 fill-primary text-primary" />
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">{t(T.hero.socialProof, lang)}</p>
              </div>
            </div>

            {/* Multi-platform reviews bar */}
            <div className="pt-2">
              <ReviewsBar lang={lang} />
            </div>
          </div>

          {/* Recent builds showcase */}
          <div className="mt-14 lg:mt-20">
            <p className="text-center text-[10px] font-heading font-bold uppercase tracking-[0.2em] text-muted-foreground mb-6">
              {lang === "en" ? "Recent builds across San Diego" : "Proyectos recientes en San Diego"}
            </p>
            <Link to="/projects" aria-label={lang === "en" ? "View completed ADU projects" : "Ver proyectos de ADUs completados"} className="group block">
              <div className="flex items-center justify-center gap-3 sm:gap-5 lg:gap-6">
                {[
                  { slide: heroSlides[0], className: "hidden sm:block w-40 md:w-52 lg:w-64 aspect-[3/4] -rotate-3 translate-y-4" },
                  { slide: heroSlides[2], className: "w-52 md:w-72 lg:w-80 aspect-[4/5] z-10 shadow-2xl" },
                  { slide: heroSlides[1], className: "hidden sm:block w-40 md:w-52 lg:w-64 aspect-[3/4] rotate-3 translate-y-4" },
                ].map(({ slide, className }) => {
                  const hasVariants = !slide.src;
                  return (
                    <div
                      key={slide.base}
                      className={`relative overflow-hidden rounded-2xl border border-border shadow-xl bg-card transition-transform duration-500 group-hover:scale-[1.02] ${className}`}
                    >
                      <img
                        src={slide.src ?? `${slide.base}-1024.webp`}
                        srcSet={hasVariants ? (slide.widths ?? [640, 1024, 1600]).map((w) => `${slide.base}-${w}.webp ${w}w`).join(", ") : undefined}
                        sizes="(max-width: 768px) 60vw, 30vw"
                        alt={slide.alt}
                        loading="lazy"
                        decoding="async"
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    </div>
                  );
                })}
              </div>
            </Link>
          </div>

          {/* Secondary navigation chips */}
          <div className="mt-12 lg:mt-16 border-t border-border/60 pt-8 flex justify-center">
            <HeroNextStep lang={lang} />
          </div>

        </div>
      </section>


      {/* ── SERVICES ─────────────────────────────────────────── */}
      <section id="services" className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-primary font-heading font-bold text-sm tracking-widest uppercase">
              {t(T.services.sectionLabel, lang)}
            </span>
            <h2 className="mt-2 text-3xl sm:text-4xl font-heading font-extrabold text-foreground">
              {t(T.services.sectionTitle, lang)}
            </h2>
            <p className="mt-4 text-muted-foreground max-w-xl mx-auto">
              {t(T.services.sectionDesc, lang)}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {T.services.items.map((s, i) => {
              const Icon = serviceIcons[i];
              return (
                <div
                  key={i}
                  className="bg-card border border-border rounded-2xl p-6 flex flex-col gap-4 hover:shadow-card hover:-translate-y-1 transition-all duration-200"
                >
                  <div className="h-12 w-12 rounded-xl bg-accent flex items-center justify-center">
                    <Icon className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="font-heading font-bold text-card-foreground">{t(s.title, lang)}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{t(s.desc, lang)}</p>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-10">
            <a href="#process">
              <Button size="lg" className="rounded-full text-base px-8">
                {t(T.services.cta, lang)}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* ── PORTFOLIO MAP ────────────────────────────────────── */}
      <section id="portfolio-map" className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="text-primary font-heading font-bold text-sm tracking-widest uppercase">
              {t(T.projects.map.sectionLabel, lang)}
            </span>
            <h2 className="mt-3 text-3xl md:text-4xl font-heading font-extrabold text-foreground leading-snug">
              {t(T.projects.map.heading, lang)}
            </h2>
            <p className="mt-3 text-muted-foreground max-w-2xl mx-auto">
              {t(T.projects.map.subheading, lang)}
            </p>
          </div>
          <Suspense fallback={<div className="h-[400px] md:h-[520px] rounded-2xl bg-muted animate-pulse" />}>
            <AduMap lang={lang} />
          </Suspense>
          <div className="text-center mt-10">
            <Link to="/projects">
              <Button size="lg" className="rounded-full text-base px-8">
                {t(T.projects.map.cta, lang)}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ─────────────────────────────────────── */}
      <section id="testimonials" className="py-20 bg-muted">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-primary font-heading font-bold text-sm tracking-widest uppercase">
              {t(T.testimonials.sectionLabel, lang)}
            </span>
            <h2 className="mt-2 text-3xl sm:text-4xl font-heading font-extrabold text-foreground">
              {t(T.testimonials.sectionTitle, lang)}
            </h2>
            <p className="mt-4 text-muted-foreground max-w-xl mx-auto">
              {t(T.testimonials.sectionDesc, lang)}
            </p>
          </div>

          {/* Video testimonials */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-card border border-border rounded-2xl overflow-hidden">
              <div className="aspect-video">
                <LiteYouTube videoId="BHrxKS4Klq4" title="George's ADU Testimonial – EG Construction" className="w-full h-full" />
              </div>
              <div className="p-4 flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                  <span className="font-heading font-bold text-primary text-sm">G</span>
                </div>
                <div>
                  <p className="font-heading font-bold text-card-foreground text-sm">George</p>
                  <div className="flex gap-0.5 mt-0.5">
                    {[1,2,3,4,5].map(i => <Star key={i} className="h-3 w-3 fill-primary text-primary" />)}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-2xl overflow-hidden">
              <div className="aspect-video">
                <LiteYouTube videoId="QJZg4jwb1ws" title="Teresa's ADU Testimonial – EG Construction" className="w-full h-full" />
              </div>
              <div className="p-4 flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                  <span className="font-heading font-bold text-primary text-sm">T</span>
                </div>
                <div>
                  <p className="font-heading font-bold text-card-foreground text-sm">Teresa</p>
                  <div className="flex gap-0.5 mt-0.5">
                    {[1,2,3,4,5].map(i => <Star key={i} className="h-3 w-3 fill-primary text-primary" />)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Written testimonials carousel */}
          <div className="relative">
            <div className="overflow-hidden rounded-2xl">
              <div
                className="flex gap-5"
                style={{
                  transform: isMobile
                    ? `translateX(calc(-${reviewIndex + offset} * (100% + 20px)))`
                    : `translateX(calc(-${reviewIndex + offset} * (100% / 3 + 20px * 2 / 3)))`,
                  transition: isTransitioning ? "transform 500ms ease-in-out" : "none",
                }}
                onTransitionEnd={() => setIsTransitioning(true)}
              >
                {clonedTestimonials.map((tm, idx) => (
                  <div key={idx} className="shrink-0 bg-card border border-border rounded-2xl p-6 flex flex-col gap-4" style={{ width: isMobile ? "100%" : "calc((100% - 40px) / 3)" }}>
                    <Quote className="h-7 w-7 text-primary/30" />
                    <p className="text-foreground/80 leading-relaxed text-sm flex-1">"{tm.text}"</p>
                    <div className="flex items-center gap-3 pt-3 border-t border-border">
                      <GoogleReviewBadge name={tm.name} size={40} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-heading font-bold text-card-foreground text-sm truncate">{tm.name}</p>
                          <a
                            href="https://share.google/oE39PeBHBc2IO4XBk"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[10px] font-heading font-bold uppercase tracking-wider text-[#4285F4] hover:underline shrink-0"
                            aria-label="Verified Google Review"
                          >
                            Google ✓
                          </a>
                        </div>
                        <p className="text-xs text-muted-foreground">{tm.location}</p>
                        <div className="flex gap-0.5 mt-0.5">
                          {Array.from({ length: tm.rating }).map((_, i) => (
                            <Star key={i} className="h-3 w-3 fill-primary text-primary" />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between mt-6">
              <button onClick={prevReview} className="h-10 w-10 rounded-full border border-border bg-card hover:border-primary hover:bg-accent flex items-center justify-center transition-colors" aria-label="Previous">
                <ChevronLeft className="h-5 w-5 text-foreground" />
              </button>
              <div className="flex gap-2">
                {testimonials.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => { setIsTransitioning(true); setReviewIndex(i); }}
                    className="relative flex items-center justify-center min-w-[24px] h-6 transition-all duration-300"
                    aria-label={`Review ${i + 1}`}
                  >
                    <span className={`block rounded-full transition-all duration-300 ${
                      ((reviewIndex % testimonials.length) + testimonials.length) % testimonials.length === i
                        ? "w-6 h-2 bg-primary"
                        : "w-2 h-2 bg-border hover:bg-muted-foreground"
                    }`} />
                  </button>
                ))}
              </div>
              <button onClick={nextReview} className="h-10 w-10 rounded-full border border-border bg-card hover:border-primary hover:bg-accent flex items-center justify-center transition-colors" aria-label="Next">
                <ChevronRight className="h-5 w-5 text-foreground" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── PROCESS ──────────────────────────────────────────── */}
      <section id="process" className="py-20 bg-muted">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-primary font-heading font-bold text-sm tracking-widest uppercase">
              {t(T.process.sectionLabel, lang)}
            </span>
            <h2 className="mt-2 text-3xl sm:text-4xl font-heading font-extrabold text-foreground">
              {t(T.process.sectionTitle, lang)}
            </h2>
            <p className="mt-4 text-muted-foreground max-w-xl mx-auto">
              {t(T.process.sectionDesc, lang)}
            </p>
          </div>

          <div className="relative">
            <div className="hidden lg:block absolute top-8 left-[calc(10%+2rem)] right-[calc(10%+2rem)] h-0.5 bg-primary/30" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
              {T.process.steps.map((step, i) => (
                <div key={i} className="flex flex-col items-center text-center gap-3">
                  <div className="relative h-16 w-16 rounded-full bg-primary flex items-center justify-center shrink-0 z-10">
                    <span className="font-heading font-extrabold text-primary-foreground text-lg">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <h3 className="font-heading font-bold text-foreground">{t(step.title, lang)}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{t(step.desc, lang)}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-12 max-w-3xl mx-auto">
            <p className="text-center text-muted-foreground text-sm mb-4 font-medium">
              {t(T.process.timelapse, lang)}
            </p>
            <div className="aspect-video rounded-2xl overflow-hidden shadow-2xl">
              <LiteYouTube videoId="ktrjRZonjx4" title="ADU Construction Timelapse – EG Construction San Diego" className="w-full h-full" />
            </div>
          </div>

          <div className="mt-10 flex justify-center">
            <Button
              onClick={() => setShowForm(true)}
              className="h-14 px-10 text-base font-heading font-bold bg-primary text-primary-foreground hover:bg-primary/90 hover:scale-105 transition-all shadow-lg"
            >
              {t(T.process.cta, lang)}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* ── BENEFITS ─────────────────────────────────────────── */}
      <section id="gallery" className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-primary font-heading font-bold text-sm tracking-widest uppercase">
              {t(T.benefits.sectionLabel, lang)}
            </span>
            <h2 className="mt-2 text-3xl sm:text-4xl font-heading font-extrabold text-foreground">
              {t(T.benefits.sectionTitle1, lang)}{" "}
              <span className="text-primary">{t(T.benefits.sectionTitle2, lang)}</span>{" "}
              {t(T.benefits.sectionTitle3, lang)}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {T.benefits.items.map((b, i) => {
              const Icon = benefitIcons[i];
              return (
                <div key={i} className="flex gap-5 p-6 bg-card border border-border rounded-2xl hover:shadow-card transition-shadow">
                  <div className="h-12 w-12 rounded-xl bg-accent flex items-center justify-center shrink-0">
                    <Icon className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-card-foreground mb-1">{t(b.title, lang)}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{t(b.desc, lang)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── IS AN ADU A GOOD IDEA VIDEO ──────────────────────── */}
      <section className="py-20 bg-background">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-primary font-heading font-bold text-sm tracking-widest uppercase">
                {t(T.video.sectionLabel, lang)}
              </span>
              <h2 className="mt-2 text-3xl sm:text-4xl font-heading font-extrabold text-foreground">
                {t(T.video.sectionTitle, lang)}
              </h2>
              <p className="mt-4 text-muted-foreground leading-relaxed">
                {t(T.video.sectionDesc, lang)}
              </p>
              <Button
                onClick={() => setShowForm(true)}
                className="mt-6 h-12 px-8 font-heading font-bold bg-primary text-primary-foreground hover:bg-primary/90"
              >
                {t(T.video.cta, lang)}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
            <div className="aspect-video rounded-2xl overflow-hidden shadow-xl">
              <LiteYouTube videoId="1ifPapuByTs" title="Is Building an ADU a Good Idea? – EG Construction San Diego" className="w-full h-full" />
            </div>
          </div>
        </div>
      </section>

      {/* ── ROI MINI CALCULATOR ──────────────────────────────── */}
      <section id="roi-mini" className="py-20 bg-muted">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="text-primary font-heading font-bold text-sm tracking-widest uppercase">
              {lang === "en" ? "Instant estimate" : "Estimado instantáneo"}
            </span>
            <h2 className="mt-2 text-3xl sm:text-4xl font-heading font-extrabold text-foreground">
              {lang === "en" ? "Will your ADU pay for itself?" : "¿Se pagará sola su ADU?"}
            </h2>
            <p className="mt-3 text-muted-foreground max-w-xl mx-auto">
              {lang === "en"
                ? "Live numbers based on real San Diego rent comps and 2026 build costs. No forms, no waiting."
                : "Números en vivo basados en comparables reales de renta en San Diego y costos de construcción 2026. Sin formularios, sin esperas."}
            </p>
          </div>
          <RoiMiniCalc lang={lang} />
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────── */}
      <section id="faq" className="py-20 bg-background">

        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-primary font-heading font-bold text-sm tracking-widest uppercase">
              {t(T.faq.sectionLabel, lang)}
            </span>
            <h2 className="mt-2 text-3xl sm:text-4xl font-heading font-extrabold text-foreground">
              {t(T.faq.sectionTitle, lang)}
            </h2>
            <p className="mt-4 text-muted-foreground max-w-xl mx-auto">
              {t(T.faq.sectionDesc, lang)}
            </p>
          </div>

          <div className="space-y-3">
            {T.faq.items.map((faq, i) => (
              <div key={i} className="bg-card border border-border rounded-xl overflow-hidden">
                <button
                  className="w-full flex items-center justify-between px-6 py-5 text-left"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span className="font-heading font-bold text-card-foreground pr-4">{t(faq.q, lang)}</span>
                  <ChevronDown className={`h-5 w-5 text-primary shrink-0 transition-transform duration-200 ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5 text-sm text-muted-foreground leading-relaxed border-t border-border pt-4">
                    {t(faq.a, lang)}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-muted-foreground mb-4">{t(T.faq.moreQuestions, lang)}</p>
            <Button
              onClick={() => setShowForm(true)}
              className="h-12 px-8 font-heading font-bold bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {t(T.faq.ctaButton, lang)}
            </Button>
          </div>
        </div>
      </section>

      {/* ── SERVICE AREAS ────────────────────────────────────── */}
      <section className="py-16 bg-background border-t border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="text-primary font-heading font-bold text-sm tracking-widest uppercase">
              {t(T.serviceAreas.sectionLabel, lang)}
            </span>
            <h2 className="mt-2 text-3xl sm:text-4xl font-heading font-extrabold text-foreground">
              {t(T.serviceAreas.sectionTitle, lang)}
            </h2>
            <p className="mt-3 text-muted-foreground max-w-xl mx-auto">
              {t(T.serviceAreas.sectionDesc, lang)}
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-3">
            {areaLinks.map((area) => (
              <Link
                key={area.slug}
                to={`/areas/${area.slug}`}
                className="flex items-center gap-2 bg-card border border-border rounded-full px-5 py-2.5 hover:border-primary hover:bg-accent transition-colors"
              >
                <MapPin className="h-3.5 w-3.5 text-primary shrink-0" />
                <span className="text-sm font-medium text-foreground">{area.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── BLOG PREVIEW ─────────────────────────────────────── */}
      <section className="py-20 bg-muted">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-primary font-heading font-bold text-sm tracking-widest uppercase">
              {lang === "en" ? "Resources" : "Recursos"}
            </span>
            <h2 className="mt-2 text-3xl sm:text-4xl font-heading font-extrabold text-foreground">
              {lang === "en" ? "Latest from Our Blog" : "Lo Último de Nuestro Blog"}
            </h2>
            <p className="mt-4 text-muted-foreground max-w-xl mx-auto">
              {lang === "en"
                ? "Expert guides on ADU costs, permits, and regulations in San Diego."
                : "Guías expertas sobre costos, permisos y regulaciones de ADU en San Diego."}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {blogPosts.slice(0, 3).map((post) => (
              <BlogCard key={post.slug} post={post} lang={lang} />
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link to="/blog">
              <Button variant="outline" className="h-12 px-8 font-heading font-bold">
                {lang === "en" ? "View All Articles" : "Ver Todos los Artículos"}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ── FINANCING ────────────────────────────────────────── */}
      <section id="financing" className="py-20 bg-background">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <span className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-1.5 rounded-full text-sm font-heading font-bold mb-4">
            <Landmark className="h-4 w-4" />
            {t(T.financing.sectionLabel, lang)}
          </span>
          <h2 className="text-3xl sm:text-4xl font-heading font-extrabold text-foreground mb-4">
            {t(T.financing.sectionTitle, lang)}
          </h2>
          <p className="text-muted-foreground mb-8 max-w-2xl mx-auto leading-relaxed">
            {t(T.financing.sectionDesc, lang)}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => setShowForm(true)}
              className="h-14 px-10 font-heading font-bold hover:scale-105 transition-all"
            >
              {t(T.financing.cta, lang)}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Link
              to="/adu-financing-san-diego"
              className="h-14 px-10 inline-flex items-center justify-center gap-2 rounded-lg border-2 border-primary text-primary font-heading font-bold hover:bg-primary hover:text-primary-foreground transition-colors"
            >
              {t(T.nav.seeAllFinancing, lang)}
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── CONTACT CTA BAND ─────────────────────────────────── */}
      <section className="py-16 bg-primary">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl font-heading font-extrabold text-primary-foreground mb-4">
            {t(T.ctaBand.title, lang)}
          </h2>
          <p className="text-primary-foreground/90 mb-8 max-w-xl mx-auto">
            {t(T.ctaBand.desc, lang)}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => setShowForm(true)}
              className="h-14 px-10 font-heading font-bold bg-secondary text-secondary-foreground hover:bg-secondary/90 hover:scale-105 transition-all"
            >
              {t(T.ctaBand.cta, lang)}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <a
              href="tel:+16198154787"
              className="h-14 px-10 inline-flex items-center justify-center gap-2 rounded-lg border-2 border-primary-foreground bg-primary-foreground text-primary font-heading font-bold hover:bg-transparent hover:text-primary-foreground transition-colors"
            >
              <Phone className="h-5 w-5" />
              {t(T.ctaBand.call, lang)}
            </a>
          </div>
        </div>
      </section>

      <ServiceTypeLinks />
      </main>

      {/* ── FOOTER ───────────────────────────────────────────── */}
      <footer className="bg-secondary text-secondary-foreground/60 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 pb-10 border-b border-secondary-foreground/10">
            <div>
              <img src={logo} alt="EG Construction" className="h-10 mb-4" width={139} height={40} />
              <p className="text-sm leading-relaxed max-w-xs">{t(T.footer.tagline, lang)}</p>
            </div>
            <div>
              <h3 className="font-heading font-bold text-secondary-foreground mb-4 text-sm uppercase tracking-widest">
                {t(T.footer.quickLinks, lang)}
              </h3>
              <ul className="space-y-2">
                {[
                  { label: t(T.nav.home, lang), href: "/" },
                  { label: t(T.nav.services, lang), href: "#services" },
                  { label: t(T.nav.ourWork, lang), href: "/projects" },
                  { label: t(T.nav.blog, lang), href: "/blog" },
                  { label: t(T.nav.getEstimate, lang), href: "/get-started" },
                ].map((l) => (
                  <li key={l.href}>
                    {l.href.startsWith("#") ? (
                      <a href={l.href} className="text-sm hover:text-secondary-foreground transition-colors">{l.label}</a>
                    ) : (
                      <Link to={l.href} className="text-sm hover:text-secondary-foreground transition-colors">{l.label}</Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="font-heading font-bold text-secondary-foreground mb-4 text-sm uppercase tracking-widest">
                {t(T.footer.contact, lang)}
              </h3>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start gap-2">
                  <Phone className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  <a href="tel:+16198154787" className="hover:text-secondary-foreground transition-colors">+1 619-815-4787</a>
                </li>
                <li className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  <span>2434 Fenton St Ste 300<br />Chula Vista, CA 91914</span>
                </li>
              </ul>
              <div className="mt-4 rounded-xl overflow-hidden border border-secondary-foreground/10">
                <iframe
                  title="EG Construction office location — Chula Vista, CA"
                  src="https://www.google.com/maps?q=2434+Fenton+St+Ste+300,+Chula+Vista,+CA+91914&output=embed"
                  width="100%"
                  height="180"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  style={{ border: 0, display: "block" }}
                />
              </div>
            </div>
          </div>
          <div className="pt-6 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs">
            <p>© 2025 EG Construction SD. All rights reserved. License #864642</p>
            <div className="flex gap-4">
              <Link to="/privacy-policy" className="hover:text-secondary-foreground transition-colors">{t(T.footer.privacy, lang)}</Link>
              <Link to="/terms-of-use" className="hover:text-secondary-foreground transition-colors">{t(T.footer.terms, lang)}</Link>
            </div>
          </div>
        </div>
      </footer>

      {/* ── FORM MODAL ───────────────────────────────────────── */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setShowForm(false)}>
          <div className="relative bg-card rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setShowForm(false)} className="absolute top-4 right-4 z-10 h-8 w-8 rounded-full bg-muted flex items-center justify-center hover:bg-muted/80 transition-colors">
              <X className="h-4 w-4 text-muted-foreground" />
            </button>
            <div className="px-6 pt-6">
              <h2 className="text-2xl font-heading font-bold text-card-foreground">{t(T.modal.title, lang)}</h2>
              <p className="text-muted-foreground mt-1 text-sm">{t(T.modal.desc, lang)}</p>
            </div>
            <iframe
              src={`https://hub.egconstructionsd.com/widget/form/MoNtjgeAw3DTpghubJuc${
                prefill.firstName || prefill.phone || prefill.timing
                  ? `?${new URLSearchParams({
                      ...(prefill.firstName ? { first_name: prefill.firstName } : {}),
                      ...(prefill.phone ? { phone: prefill.phone } : {}),
                      ...(prefill.timing ? { timing: prefill.timing } : {}),
                    }).toString()}`
                  : ""
              }`}

              style={{ width: "100%", height: "700px", border: "none" }}
              id="inline-MoNtjgeAw3DTpghubJuc"
              data-layout='{"id":"INLINE"}'
              data-trigger-type="alwaysShow"
              data-activation-type="alwaysActivated"
              data-deactivation-type="neverDeactivate"
              data-form-name="Website New Form - Google Ads Landing"
              data-height="700"
              data-layout-iframe-id="inline-MoNtjgeAw3DTpghubJuc"
              data-form-id="MoNtjgeAw3DTpghubJuc"
              title="Website New Form - Google Ads Landing"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
